// Used in side-nav.component.html and header.component.html

// Angular
import {
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";

// RXJS/Store
import * as fromCoursesStore from "#core/teacher-classes/store";
import * as profileStore from "#login/store";
import { Store, select } from "@ngrx/store";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

// Services/Types/Helpers
import { AssignmentHelper } from "#helpers/assignment.helper";
import { LanguageHelper } from "#helpers/language.helper";
import { SubjectId } from "@carnegie-learning/common-types";
import { PublicService } from "@carnegie-learning/data-access-angular-passport";
import { Assignment, CompositeTaskAssignment, PPUser as User } from "cli-types";

@Component({
    selector: "user-profile",
    templateUrl: "./profile-side-panel.component.html",
    styleUrls: [
        "./profile-side-panel.component.scss",
        "../../../../core/styles/shared-styles.scss",
    ],
    standalone: false
})
export class ProfileSidePanelComponent implements OnInit, OnChanges, OnDestroy {
    userProfile?: User;
    profileName = "";
    localTimezoneDisplay = "";
    localTimezone = "";
    timezoneMismatch = false;
    private ngUnsubscribe = new Subject();
    isMath = false;
    isStudent = false;

    public frontendBuildNumber: number;
    public apiBuildNumber: number;

    @Input() schoolTimezone = "";
    @Input() viewAsStudent = false;
    @Input() imgSize = "";
    @Input() subjectId: SubjectId = "";
    @Input() selectedAssignment?: Assignment | CompositeTaskAssignment;

    constructor(
        private assignmentHelper: AssignmentHelper,
        public profile_store: Store<profileStore.UserState>,
        private coursesStore: Store<fromCoursesStore.CoursesState>,
        private router: Router,
        private route: ActivatedRoute,
        private languageHelper: LanguageHelper,
        private publicService: PublicService
    ) {
        this.profile_store.dispatch(profileStore.userProfileRefresh());
        this.profile_store
            .pipe(select(profileStore.getUserProfile))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((userProfile) => {
                if (userProfile) {
                    this.localTimezoneDisplay = userProfile.timezone.replace(
                        "_",
                        " "
                    );
                    this.localTimezone = userProfile.timezone;
                    this.userProfile = userProfile;
                    if (userProfile.firstName) {
                        this.profileName =
                            userProfile.firstName +
                            " " +
                            userProfile.lastName.charAt(0) +
                            ".";
                    } else {
                        this.profileName = "";
                    }
                }
            });
        this.profile_store
            .pipe(select(profileStore.isStudent))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((isStudent) => {
                this.isStudent = isStudent;
            });
    }

    ngOnInit() {
        this.isMath = this.languageHelper.ifMathLanguage(this.subjectId);

        this.frontendBuildNumber = (<any>window).buildNumber;

        this.publicService
            .getVersion()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((response) => {
                this.apiBuildNumber = response.version;
            });
    }

    ngOnChanges() {
        // Determine if your timezone is different from your school's timezone.
        if (this.localTimezone && this.schoolTimezone) {
            this.timezoneMismatch = this.localTimezone !== this.schoolTimezone;
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }

    editProfile() {
        this.profile_store
            .pipe(select(profileStore.isStudent))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((isStudent) => {
                if (isStudent) {
                    this.router.navigate(["/student/profile/edit-student"], {
                        relativeTo: this.route,
                    });
                } else {
                    this.router.navigate(["profile/edit"], {
                        relativeTo: this.route,
                    });
                }
            });
    }

    logout() {
        if (this.userProfile && this.userProfile.roleLevel < 5) {
            // Only non-students we set to null
            this.coursesStore.dispatch(
                fromCoursesStore.setSelectedCourse({ payload: null })
            );
        }
        this.profile_store.dispatch(profileStore.userLogout({ signout: true }));
    }

    getHelpCenterUrl() {
        if (this.userProfile) {
            const roleLevel = this.userProfile.roleLevel;
            if (roleLevel === 5) {
                // If Student - https://www.carnegielearning.com/help-center/
                return "https://www.carnegielearning.com/help-center/";
            } else if (roleLevel === 4) {
                // If Teacher - https://www.carnegielearning.com/help-center/
                return "https://www.carnegielearning.com/help-center/";
            }
        }
    }
}
